import * as React from 'react';

import * as Entri from 'entrijs';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {SingleInputModal} from 'shells/single-input-modal/single-input-modal';

import {addNotification} from '../../../notifications/index';
import {usePoll} from '../../../util/hooks';

import {useGetSubdomains} from '../../core/subdomains/graphql/hooks/use-get-subdomains';
import {BannerCueCards} from '../../glossary/layout/banner-cue-cards';

import {SettingsSidebar} from '../settings-sidebar';

import {AddDomainCard} from './add-domain-card';
import {DomainsTable} from './domains-table';
import {FeatureStatuses} from './feature-statuses';
import {
    useGetDomains,
    useDeleteDomain,
    useCreateEntriConfig,
    useGetDomainFeatureStatuses,
} from './graphql/hooks';

// @ts-expect-error
let STOP_POLLING_TIMEOUT_ID;

export function DomainsPage() {
    const [isAddingDomain, setIsAddingDomain] = React.useState<boolean>(false);
    const [domainInEntriFlow, setDomainInEntriFlow] = React.useState<string | null | undefined>(
        null
    );
    const [expandedDomain, setExpandedDomain] = React.useState<number | null | undefined>(
        undefined
    );
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);
    const [shouldPollForNewDomain, setShouldPollForNewDomain] = React.useState<boolean>(false);

    const {domains, isLoading, error, startPolling, stopPolling} = useGetDomains();
    const {subdomains, refetch: refetchSubdomains} = useGetSubdomains();
    const {
        domainFeatureStatuses,
        isLoading: isLoadingDomainFeatureStatuses,
        refetch: refetchDomainFeatureStatuses,
    } = useGetDomainFeatureStatuses();

    const {deleteDomain} = useDeleteDomain();
    const {createEntriConfig, isCreating: isCreatingEntriConfig} = useCreateEntriConfig();

    const onDeleteDomain = (domainName: string) => {
        return deleteDomain({name: domainName});
    };

    const onCloseModal = () => {
        setErrorMessage(null);
        setIsAddingDomain(false);
    };

    const handleAddDomainUsingEntri = (domain: string) => {
        setDomainInEntriFlow(domain);
        setErrorMessage(null);
        createEntriConfig({domain: domain})
            .then((config) => {
                setIsAddingDomain(false);

                if (config) {
                    return Entri.showEntri(config);
                }

                setDomainInEntriFlow(null);
                addNotification('', {
                    body: 'An unexpected error occurred. Please try again later.',
                    icon: 'frowny',
                });
            })
            .catch((err) => {
                setDomainInEntriFlow(null);
                setErrorMessage(safelyGetGraphQLErrorMessage(err));
            });
    };

    const handleEntriClose = React.useCallback((event) => {
        setDomainInEntriFlow(null);

        if (event.detail.success) {
            setShouldPollForNewDomain(true);

            // @ts-expect-error
            if (STOP_POLLING_TIMEOUT_ID) {
                clearTimeout(STOP_POLLING_TIMEOUT_ID);
            }

            STOP_POLLING_TIMEOUT_ID = setTimeout(() => {
                setShouldPollForNewDomain(false);
            }, 10000);
        }
    }, []);

    React.useEffect(() => {
        window.addEventListener('onEntriClose', handleEntriClose);

        return () => {
            window.removeEventListener('onEntriClose', handleEntriClose);
        };
    }, [handleEntriClose]);

    React.useEffect(() => {
        if (domains) {
            setShouldPollForNewDomain(false);
            refetchDomainFeatureStatuses();
            refetchSubdomains();
        }
    }, [domains, refetchDomainFeatureStatuses, refetchSubdomains]);

    usePoll({
        shouldPoll: shouldPollForNewDomain,
        startPolling,
        stopPolling,
    });

    const hasDomains = domains.length > 0 && !isLoading;
    const signupDomain = NutClientConfig.signupDomain || '';

    return (
        <div>
            <BannerCueCards />
            <div className='flex'>
                <div className='mr-16 flex-grow'>
                    <StackLegacy spacing={16}>
                        {!hasDomains && <AddDomainCard onClick={() => setIsAddingDomain(true)} />}
                        {hasDomains && (
                            <DomainsTable
                                domains={domains}
                                onAddRowClick={() => setIsAddingDomain(true)}
                                onDeleteDomain={onDeleteDomain}
                                isLoading={isLoading}
                                error={error}
                                expandRowIndex={expandedDomain}
                                clearExpandRowIndex={() => setExpandedDomain(undefined)}
                                handleEntriRetry={handleAddDomainUsingEntri}
                                domainInEntriFlow={domainInEntriFlow}
                            />
                        )}
                        <FeatureStatuses
                            subdomains={subdomains}
                            domainFeatureStatuses={domainFeatureStatuses}
                            isLoading={isLoadingDomainFeatureStatuses}
                            onFeatureUpdate={refetchDomainFeatureStatuses}
                            onAddDomain={() => setIsAddingDomain(true)}
                        />
                    </StackLegacy>
                </div>
                <div style={{width: '240px'}}>
                    <SettingsSidebar />
                </div>
            </div>
            {isAddingDomain && (
                <SingleInputModal
                    requireValue={true}
                    buttonText='Start setup'
                    title='Add a new domain'
                    subtitle='Nutshell will guide you through a quick domain setup to use email marketing'
                    placeholder='example.com'
                    initialValue={hasDomains ? '' : signupDomain}
                    savingText='Starting setup...'
                    isSaving={isCreatingEntriConfig}
                    errorMessage={errorMessage}
                    onClose={onCloseModal}
                    onSave={(domain: string) => {
                        handleAddDomainUsingEntri(domain);
                    }}
                />
            )}
        </div>
    );
}
